import React, { useMemo, useState } from "react";
import { createUseStyles } from "react-jss";
import { Style, ViewCompact, ViewStream } from "@mui/icons-material";
import { Button, Drawer, Box, Tooltip, FormControlLabel, Switch, Typography } from "@mui/material";
import { Headphones, Info } from "lucide-react";
import { Sections } from "./Sections";
import {
  useScrollyTellProps,
  useScrollytellDispatch,
  useScrollytellTranslation,
  SET_A11Y_DRAWER_OPEN,
} from "./scrollytellContext";
import { inspectStory } from "../A11yInspector/A11yInspectorEngine";
import { A11yStatusBadge } from "../A11yInspector/A11yStatusBadge";

const useStyles = createUseStyles({
  sidebar: {
    width: "280px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#1E293B",
    height: "100%",
    overflow: "hidden",
  },
  zoneHeader: {
    flexShrink: 0,
    backgroundColor: "#0F172A",
    padding: "10px 12px",
    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    boxSizing: "border-box",
  },
  headerTopRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle: { fontSize: "11px", fontWeight: 700, color: "#94A3B8", letterSpacing: "0.5px", textTransform: "uppercase" },
  toggleGroup: { display: "flex", backgroundColor: "rgba(255, 255, 255, 0.08)", borderRadius: "4px", padding: "2px", gap: "2px" },
  toggleBtn: {
    border: "none", background: "none", cursor: "pointer", borderRadius: "3px", padding: "3px 6px",
    display: "flex", alignItems: "center", color: "#94A3B8", fontSize: "11px",
    "&.active": { backgroundColor: "#4484F1", color: "#FFFFFF", fontWeight: 600 },
    "&:hover": { color: "#FFFFFF" },
  },
  zoneScrollable: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    "--scrollbar-thumb": "rgba(148, 163, 184, 0.4)",
    "--scrollbar-track": "transparent",
    colorScheme: "dark",
    scrollbarWidth: "thin",
    scrollbarGutter: "stable",
    scrollbarColor: "var(--scrollbar-thumb) var(--scrollbar-track)",
    "@media (prefers-contrast: more)": {
      "--scrollbar-thumb": "#FFFFFF",
      "--scrollbar-track": "#0F172A",
    },
    "@supports not (scrollbar-color: auto)": {
      "&::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: "var(--scrollbar-track)",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "var(--scrollbar-thumb)",
        borderRadius: "999px",
      },
    },
  },
});

export const Sidebar = ({ sections = [], currentSection, isOpen }) => {
  const t = useScrollytellTranslation();
  const { onTriggerSettings, story, onChange } = useScrollyTellProps();
  const dispatch = useScrollytellDispatch();
  const classes = useStyles();
  const [compactMode, setCompactMode] = useState(false);

  const handleToggleAutoplayFeature = (event) => {
    const enabled = event.target.checked;
    if (onChange && story) {
      onChange({
        ...story,
        autoplay: { ...(story.autoplay || {}), enabled },
        audioGuide: { ...(story.audioGuide || {}), enabled },
      });
    }
  };

  const { issues, criticalCount, warningCount } = useMemo(() => inspectStory(story), [story]);

  return (
    <Drawer
      anchor="left"
      hideBackdrop={true}
      variant="persistent"
      ModalProps={{ keepMounted: true }}
      sx={{
        flexShrink: 0,
        width: !isOpen ? "0" : "280px",
        transition: "width 150ms ease-in",
        [`& .MuiDrawer-paper`]: {
          position: "initial",
          boxSizing: "border-box",
          overflowX: "hidden",
          width: !isOpen ? "0" : "280px",
        },
      }}
      open={isOpen}
      transitionDuration={150}
    >
      <div className={classes.sidebar}>
        {/* Zone 1: Sticky Top Header */}
        <div className={classes.zoneHeader}>
          <div className={classes.headerTopRow}>
            <span className={classes.headerTitle}>
              {t("sectionsHeader", "Seksjoner")} ({sections?.length || 0})
            </span>
            <div className={classes.toggleGroup}>
              <button
                type="button"
                className={`${classes.toggleBtn} ${!compactMode ? "active" : ""}`}
                onClick={() => setCompactMode(false)}
                data-testid="sidebar-view-toggle-mini"
                title={t("viewModeThumbnails", "Miniatyrvisning")}
              >
                <ViewStream sx={{ fontSize: 14 }} />
              </button>
              <button
                type="button"
                className={`${classes.toggleBtn} ${compactMode ? "active" : ""}`}
                onClick={() => setCompactMode(true)}
                data-testid="sidebar-view-toggle-compact"
                title={t("viewModeCompact", "Kompakt listevisning")}
              >
                <ViewCompact sx={{ fontSize: 14 }} />
              </button>
            </div>
          </div>

          <A11yStatusBadge
            issueCount={issues.length}
            criticalCount={criticalCount}
            warningCount={warningCount}
            onClick={() => dispatch({ type: SET_A11Y_DRAWER_OPEN, open: true })}
          />

          <Button
            onClick={() => onTriggerSettings()}
            sx={{
              width: "100%", borderRadius: "6px", padding: "6px 10px", margin: 0,
              justifyContent: "flex-start", gap: "8px", textTransform: "none",
              fontSize: "12.5px", fontWeight: 600, backgroundColor: "#334155",
              border: "1px solid rgba(255, 255, 255, 0.14)", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#475569" },
            }}
          >
            <Style sx={{ fontSize: 18, color: "#93C5FD" }} />
            {t("sidebarOptionButtonText", "Fonter og farger")}
          </Button>

          <Box
            sx={{
              width: "100%", borderRadius: "6px", padding: "4px 6px 4px 10px",
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.14)",
              display: "flex", alignItems: "center", justifyContent: "space-between", boxSizing: "border-box",
            }}
          >
            <FormControlLabel
              sx={{
                margin: 0, width: "100%", display: "flex", justifyContent: "space-between",
                alignItems: "center", flexDirection: "row-reverse",
                "& .MuiFormControlLabel-label": { width: "100%" },
              }}
              control={
                <Switch
                  size="small"
                  checked={Boolean(story?.autoplay?.enabled || story?.audioGuide?.enabled)}
                  onChange={handleToggleAutoplayFeature}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Headphones size={16} color="#FFFFFF" />
                  <Typography sx={{ color: "#FFFFFF", fontSize: "12px", fontWeight: 500, lineHeight: 1.2 }}>
                    {t("Spill av historien", "Lydguide og autoscroll")}
                  </Typography>
                  <Tooltip title={t("AudioGuideAdminDescription", "Vis avspillingslinje med opplesing og automatisk rulling for publikum")}>
                    <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
                      <Info size={14} color="rgba(255,255,255,0.65)" />
                    </Box>
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        </div>

        {/* Zone 2: Scrollable Middle Section List */}
        <div className={classes.zoneScrollable}>
          <Sections
            sections={sections}
            currentSection={currentSection}
            compactMode={compactMode}
          />
        </div>
      </div>
    </Drawer>
  );
};
